import { Button } from "@/components/ui/button"
import { DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog"
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form"
import { ScrollArea } from "@/components/ui/scroll-area"
import { Textarea } from "@/components/ui/textarea"
import { Checkbox } from "@/components/ui/checkbox"
import { PlusCircle, X } from "lucide-react"
import type { DiagnosticActivity } from "@/services/api/odin/activities/diagnostic/study-session/update"
import { useForm } from "react-hook-form"
import { zodResolver } from "@hookform/resolvers/zod"
import { useUpdateDiagnostic } from "../../queries/use-update-diagnostic"
import {
  type UpdateMultipleChoiceFormValues,
  updateMultipleChoiceSchema,
} from "../../validations/update-multiple-choice"
import { useParams } from "react-router-dom"

const MAX_OPTIONS = 4

interface MultipleChoiceFormProps {
  activity: DiagnosticActivity
  onUpdateActivity: (question: string, options: string[]) => void
  onClose: () => void
}

export default function MultipleChoiceForm({
  activity,
  onUpdateActivity,
  onClose,
}: MultipleChoiceFormProps) {
  const { isPending, mutateAsync: updateActivity } = useUpdateDiagnostic()
  const { id } = useParams()
  // Transform activity.next.options into the format we need
  const initialOptions = activity.next?.options
    ? activity.next.options.map((option) => ({
        text: option,
        isCorrect: false,
      }))
    : [
        { text: "", isCorrect: true },
        { text: "", isCorrect: false },
      ]

  const form = useForm<UpdateMultipleChoiceFormValues>({
    resolver: zodResolver(updateMultipleChoiceSchema),
    defaultValues: {
      question: activity.next?.question || "",
      options: initialOptions,
    },
  })

  const {
    control,
    handleSubmit,
    formState: { errors },
    setValue,
    getValues,
    watch,
  } = form

  const onSubmit = async (data: UpdateMultipleChoiceFormValues) => {
    const formattedOptions = data.options.map((option) => option.text)
    const correctOptionIndex = data.options.findIndex((option) => option.isCorrect)

    await updateActivity({
      id: id as string,
      question: data.question || "",
      index: activity.current,
      payload: {
        options: formattedOptions,
        answerIndex: correctOptionIndex,
      },
    })
    onUpdateActivity(data.question, formattedOptions)
    onClose()
  }

  const addOption = () => {
    const currentOptions = getValues("options")
    if (currentOptions.length >= MAX_OPTIONS) return
    setValue("options", [...currentOptions, { text: "", isCorrect: false }])
  }

  const removeOption = (index: number) => {
    const currentOptions = getValues("options")
    if (currentOptions.length <= 2) return // Maintain at least 2 options

    const newOptions = [...currentOptions]
    newOptions.splice(index, 1)
    setValue("options", newOptions)
  }

  const handleCorrectChange = (index: number, checked: boolean) => {
    if (checked) {
      // Uncheck all other options
      const options = getValues("options")
      options.forEach((_, i) => {
        setValue(`options.${i}.isCorrect`, i === index)
      })
    }
  }

  // Get letter based on index (A, B, C, etc.)
  const getLetter = (index: number) => String.fromCharCode(65 + index)

  const canAddMoreOptions = watch("options").length < MAX_OPTIONS

  return (
    <>
      <DialogHeader className="flex flex-row items-center justify-between border-b border-neutral-200 pb-4 pl-4">
        <DialogTitle>Editar pregunta</DialogTitle>
      </DialogHeader>
      <Form {...form}>
        <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
          <ScrollArea className="h-[300px]">
            <div className="space-y-4 px-6">
              <FormField
                control={control}
                name="question"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Enunciado</FormLabel>
                    <FormControl>
                      <Textarea placeholder="Escribe la pregunta aquí..." {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <div className="space-y-4">
                <FormLabel>Opciones</FormLabel>

                {watch("options").map((_, index) => (
                  <div key={index} className="h-fit rounded-md border border-neutral-200 p-2">
                    <div className="mb-2 flex items-start gap-4">
                      <FormField
                        control={control}
                        name={`options.${index}.isCorrect`}
                        render={({ field }) => (
                          <Checkbox
                            checked={field.value}
                            onCheckedChange={(checked) => {
                              field.onChange(checked)
                              handleCorrectChange(index, checked as boolean)
                            }}
                          />
                        )}
                      />
                      <div className="inline-flex flex-1 items-start">
                        <span className="text-sm font-medium">{getLetter(index)}.</span>
                        <FormField
                          control={control}
                          name={`options.${index}.text`}
                          render={({ field }) => (
                            <FormItem className="flex-1">
                              <FormControl>
                                <Textarea
                                  placeholder={`Escribe la opción ${getLetter(index)}`}
                                  {...field}
                                  className="h-auto min-h-[60px] w-full border-none py-0 shadow-none"
                                  rows={4}
                                />
                              </FormControl>
                              <FormMessage />
                            </FormItem>
                          )}
                        />
                      </div>
                      <Button
                        type="button"
                        size="icon"
                        variant="ghost"
                        onClick={() => removeOption(index)}
                        disabled={watch("options").length <= 2}
                        className="h-5 w-5"
                      >
                        <X className="h-3.5 w-3.5" />
                      </Button>
                    </div>
                  </div>
                ))}

                {errors.options && (
                  <p className="text-sm font-medium text-destructive">
                    {errors.options.message || "Debe seleccionar al menos una opción correcta"}
                  </p>
                )}
              </div>

              {canAddMoreOptions && (
                <Button
                  type="button"
                  size="sm"
                  variant="outline"
                  onClick={addOption}
                  className="flex w-full justify-start gap-2 text-muted-foreground hover:text-muted-foreground"
                >
                  <PlusCircle className="h-3.5 w-3.5" />
                  Agregar opción
                </Button>
              )}
            </div>
          </ScrollArea>

          <DialogFooter className="border-t border-neutral-200 px-4 pt-4">
            <Button
              type="submit"
              size="sm"
              className="self-end bg-primary-500"
              disabled={isPending}
            >
              Guardar cambios
            </Button>
          </DialogFooter>
        </form>
      </Form>
    </>
  )
}
